/**
 * Copyright 2024-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo } from "react";
import { theme } from "@nordcloud/gnui";
import {
  EventActionAttempt,
  ResourceExecutionErrorCodeCount,
  useEventActionStatusChangedSubscription,
} from "~/generated/graphql";
import { BaselineTag, ActionNameDisplay } from "~/components";
import { baselineTagToShow } from "~/utils";
import { EventToggleItemBox } from "~/views/events/components/EventToggleItemBox/EventToggleItemBox";
import { getEventEntityStatusWithAttention } from "~/views/events/helpers";
import { useGetEventView } from "~/views/events/hooks";
import { EventEntity, FlatEventEntity } from "~/views/events/types";
import { useEvent } from "../../../EventProvider";

type Props = {
  item: EventEntity;
  onMouseDown: () => void;
  attempt?: EventActionAttempt;
  handleSelectAttempt: (value: EventActionAttempt | undefined) => void;
  eventEntity: EventEntity;
  flatEventEntities?: FlatEventEntity[];
  errorCodeCounts?: ResourceExecutionErrorCodeCount[];
};

export function EventEntityItem({
  item,
  onMouseDown,
  attempt,
  handleSelectAttempt,
  eventEntity,
  flatEventEntities,
  errorCodeCounts = [],
}: Props) {
  const { event: eventCtx } = useEvent();
  const { refetch: refetchEvent } = useGetEventView({
    eventId: eventCtx.id,
  });

  const attempts =
    item?.attempts && item.attempts?.length > 1 ? item.attempts : [];
  const latestAttemptId =
    item.attempts && item.attempts.length > 0
      ? item.attempts[item.attempts.length - 1].id
      : "";

  const { data } = useEventActionStatusChangedSubscription({
    variables: {
      input: {
        // globalState is recalculated at product api - needs to be refetched when latest attempt status changes
        id: latestAttemptId,
      },
    },
    skip: !latestAttemptId,
    onData: () => refetchEvent(),
  });

  const liveStatus = data?.eventActionStatusChanged ?? item.globalState?.status;
  const statusDetails = getEventEntityStatusWithAttention({
    status: liveStatus,
    errorCodeCounts,
  });

  const baselineTag = useMemo(
    () => baselineTagToShow(item, flatEventEntities ?? []),
    [item, flatEventEntities]
  );

  return (
    <EventToggleItemBox
      item={{
        id: item.id,
        color: item.action?.color ?? theme.color.support.grey,
        name: (
          <ActionNameDisplay
            actionName={item.action?.name || ""}
            customName={item.name}
            actionType={item.type}
          />
        ),
        icon: statusDetails.icon,
        iconColor: statusDetails.color(),
        iconPathColors: statusDetails.pathColors,
        tooltip: statusDetails.description,
        tooltipColor: statusDetails.tooltipColor,
        onMouseDown: onMouseDown,
        onSelect: handleSelectAttempt,
      }}
      attempts={attempts}
      selectedAttempt={attempt}
      active={item.id === eventEntity.id}
      tag={baselineTag ? <BaselineTag tag={baselineTag} /> : undefined}
    />
  );
}
